import { X, RefreshCw, CheckCircle, XCircle, Monitor, Moon, Sun } from 'lucide-react';
import { sidebar } from './sidebarStyles';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import { cn } from './ui/utils';
import { useTheme } from '../context/ThemeContext';

type CommandApiStatus = 'idle' | 'checking' | 'connected' | 'disconnected';

export interface ClinicianSettings {
  density: string;
  evidenceFooters: boolean;
  /** Legacy keys kept optional so persisted settings objects stay assignable. */
  defaultTimeWindow?: string;
  modelVersion?: boolean;
  keyboardShortcuts?: boolean;
}

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  /** When true, render as inline sidebar panel (no overlay) */
  asSidebar?: boolean;
  sidebarWidth?: number;
  settings: ClinicianSettings;
  onSettingsChange: (settings: ClinicianSettings) => void;
  commandApiEnabled?: boolean;
  commandApiStatus?: CommandApiStatus;
  onCheckCommandApi?: () => void;
}

const DENSITY_OPTIONS: Array<{ value: 'compact' | 'standard' | 'detailed'; label: string; hint: string }> = [
  { value: 'compact', label: 'Compact', hint: 'Key facts only' },
  { value: 'standard', label: 'Standard', hint: 'Balanced detail' },
  { value: 'detailed', label: 'Detailed', hint: 'Full reasoning' },
];

function Toggle({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-2.5 py-2">
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-medium text-foreground">{label}</div>
        <div className="text-[10px] leading-4 text-text-tertiary">{hint}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative h-4 w-9 shrink-0 rounded-full transition-colors',
          checked ? sidebar.toggleOn : sidebar.toggleOff,
        )}
      >
        <span
          className={cn(
            'absolute left-0.5 top-0.5 h-3.5 w-3.5 rounded-full bg-white transition-transform',
            checked ? 'translate-x-5' : 'translate-x-0',
          )}
        />
      </button>
    </div>
  );
}

/**
 * Clinician workspace settings. Everything here is a personal display
 * preference — nothing clinical or account-level lives in this panel, and
 * every control says what it actually does (the old panel labeled the chart
 * time window "Data retention policy", which it never was).
 */
export function SettingsModal({
  open,
  onClose,
  asSidebar = false,
  sidebarWidth,
  settings,
  onSettingsChange,
  commandApiEnabled = false,
  commandApiStatus = 'idle',
  onCheckCommandApi,
}: SettingsModalProps) {
  const { theme, setTheme } = useTheme();

  // In-flow sidebar mode has no Radix state machine to manage visibility.
  // Overlay mode keeps the Dialog root mounted and drives `open`, so Radix can
  // run its close sequence (focus restore to the trigger).
  if (asSidebar && !open) return null;

  const update = <K extends keyof ClinicianSettings>(key: K, value: ClinicianSettings[K]) => {
    onSettingsChange({ ...settings, [key]: value });
  };

  const subtitle = 'Personal display preferences — stored on this device';
  const subtitleClass = 'truncate text-[10px] text-text-tertiary normal-case tracking-normal';

  // In overlay mode the title/subtitle double as the dialog's accessible
  // name/description, so they must be Radix Title/Description primitives.
  const header = (overlay: boolean) => (
    <div className={overlay ? 'border-b border-border flex items-center justify-between shrink-0 sticky top-0 z-10 px-3 sm:px-6 py-2 sm:py-4 bg-surface-raised' : sidebar.header}>
      <div className="min-w-0">
        {overlay ? (
          <DialogTitle className={sidebar.title}>Settings</DialogTitle>
        ) : (
          <h2 className={sidebar.title}>Settings</h2>
        )}
        {overlay ? (
          <DialogDescription className={subtitleClass}>{subtitle}</DialogDescription>
        ) : (
          <p className={subtitleClass}>{subtitle}</p>
        )}
      </div>
      <button onClick={onClose} className={sidebar.closeButton} aria-label="Close settings">
        <X className="w-3 h-3" />
      </button>
    </div>
  );

  const body = (asSidebarBody: boolean) => (
    <>
      <div className={`${sidebar.body} ${asSidebarBody ? sidebar.bodyPad : 'px-3 sm:px-6 py-3 sm:py-6'} space-y-4`}>
        {/* APPEARANCE */}
        <div>
          <p className={sidebar.sectionLabel}>Appearance</p>
          <div className="flex gap-1" role="radiogroup" aria-label="Theme">
            {(
              [
                { value: 'light', label: 'Light', Icon: Sun },
                { value: 'dark', label: 'Dark', Icon: Moon },
                { value: 'system', label: 'System', Icon: Monitor },
              ] as const
            ).map(({ value, label, Icon }) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={theme === value}
                onClick={() => setTheme(value)}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1 rounded-md border px-2 py-1.5 text-[11px] font-medium transition-colors',
                  theme === value ? sidebar.pillSolidSelected : sidebar.pillDefault,
                )}
              >
                <Icon className="h-3 w-3" aria-hidden />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* RESPONSE DETAIL */}
        <div>
          <p className={sidebar.sectionLabel}>Response detail</p>
          <div className="flex gap-1" role="radiogroup" aria-label="Response detail">
            {DENSITY_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={settings.density === option.value}
                onClick={() => update('density', option.value)}
                className={cn(
                  'flex-1 rounded-md border px-2 py-1.5 text-left transition-colors',
                  settings.density === option.value ? sidebar.pillSolidSelected : sidebar.pillDefault,
                )}
              >
                <span className="block text-[11px] font-medium">{option.label}</span>
                <span className="block text-[9px] opacity-80">{option.hint}</span>
              </button>
            ))}
          </div>
        </div>

        {/* RESPONSE EXTRAS */}
        <div>
          <p className={sidebar.sectionLabel}>Responses</p>
          <div className={`${sidebar.card} divide-y divide-border`}>
            <Toggle
              checked={settings.evidenceFooters}
              onChange={(v) => update('evidenceFooters', v)}
              label="Evidence footers"
              hint="Show sources and confidence under each answer"
            />
          </div>
        </div>

        {/* CONNECTION */}
        {commandApiEnabled && (
          <div>
            <p className={sidebar.sectionLabel}>Connection</p>
            <div className={`${sidebar.card} flex items-center gap-2 px-2.5 py-2`}>
              {commandApiStatus === 'connected' ? (
                <CheckCircle className="h-4 w-4 shrink-0 text-status-positive-text" aria-hidden />
              ) : commandApiStatus === 'disconnected' ? (
                <XCircle className="h-4 w-4 shrink-0 text-status-negative-text" aria-hidden />
              ) : (
                <RefreshCw className={cn('h-4 w-4 shrink-0 text-status-attention-text', commandApiStatus === 'checking' && 'animate-spin')} aria-hidden />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-medium text-foreground">
                  {commandApiStatus === 'connected' && 'Connected'}
                  {commandApiStatus === 'checking' && 'Checking…'}
                  {commandApiStatus === 'disconnected' && 'Backend unavailable'}
                  {commandApiStatus === 'idle' && 'Not checked'}
                </div>
              </div>
              {onCheckCommandApi && (
                <button
                  type="button"
                  onClick={onCheckCommandApi}
                  disabled={commandApiStatus === 'checking'}
                  className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-primary hover:underline disabled:opacity-50"
                >
                  <RefreshCw className={cn('h-3.5 w-3.5', commandApiStatus === 'checking' && 'animate-spin')} aria-hidden />
                  Verify
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className={`border-t border-border shrink-0 ${asSidebarBody ? 'px-2 py-1.5' : 'px-3 sm:px-6 py-3 sm:py-4'}`}>
        <button onClick={onClose} className={sidebar.doneButton}>
          Done
        </button>
      </div>
    </>
  );

  if (asSidebar) {
    return (
      <div style={sidebarWidth ? { width: `${sidebarWidth}px` } : undefined} className={sidebar.root}>
        {header(false)}
        {body(true)}
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[90vh] w-full max-w-md flex-col gap-0 overflow-y-auto border-border bg-surface-raised p-0 sm:max-h-[85vh] sm:max-w-md sm:rounded-xl"
      >
        {header(true)}
        {body(false)}
      </DialogContent>
    </Dialog>
  );
}
